import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobile } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { TimelineItemType } from 'types/TimelineTypes';
import { formatTimelineDate } from 'services/utils/dateTimeUtils';
import { saveData } from 'services/api/saveData';
import SmsMessageViewer from './SmsMessageViewer';
import '../../style/scss/tooltip.scss';
import '../../style/scss/sms.scss';

// The shape of an sms message in the timeline
export interface TimelineSmsMessage {
    id: number;
    message_type: string;
    status: string;
    skip_reason?: string | null;
    scheduled_at?: string | null;
    sent_at?: string | null;
    phone_number?: string | null;
    body?: string | null;
    template_name?: string | null;
    recipient_name?: string | null;
    sent_by_user_name?: string | null;
    covered_dates?: string[];
    preview_body?: string | null;
    preview_recipient_name?: string | null;
    preview_phone_number?: string | null;
}

interface TimelineSmsProps {
    placement: 'summary' | 'page';
    item: TimelineItemType;
    smsMessage: TimelineSmsMessage;
    onSubmitSuccess?: (newItem: TimelineItemType) => void;
}

const TimelineSms: React.FC<TimelineSmsProps> = ({ placement, item, smsMessage, onSubmitSuccess }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();

    // Determine the display state of the message
    const isScheduled = smsMessage.status === 'scheduled' || smsMessage.status === 'retrying';

    // Format the date to show, the planned moment or the sent moment
    const displayDate = isScheduled
        ? formatTimelineDate(smsMessage.scheduled_at || item.ordering_date, false)
        : formatTimelineDate(smsMessage.sent_at || item.ordering_date, false);

    // Build the title of the timeline item
    const title = smsMessage.template_name || t(`sms_template.message_type.${smsMessage.message_type}`);

    // Open the sms message viewer modal
    const handleOpenSmsViewer = () => {
        initializeModal(
            <SmsMessageViewer smsMessage={smsMessage} displayDate={displayDate} />,
            { modalSize: 'small-medium', title }
        );
    };

    // Render the title with the scheduled badge
    const renderTitle = () => (
        <div className='email-subject' onClick={handleOpenSmsViewer}>
            {title}
            {isScheduled && (
                <span className='sms-scheduled-badge'>
                    {t('timeline.sms.scheduled_badge')}
                </span>
            )}
        </div>
    );

    // Cancel the scheduled message and remove it from the timeline
    const handleCancel = async (event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            // Cancel the message in the backend
            const response = await saveData({
                apiUrl: `cancel_sms_message/${smsMessage.id}`,
                method: 'post',
                data: {}
            });

            // Remove the item from the timeline state
            if (response?.status === 200) {
                setFloatingAlert({ type: 'success' });
                onSubmitSuccess?.({ ...item, deleted: true });
            }
        } catch (error) {
            console.error(error);
        }
    };

    // Render the date and sender info line with the cancel action
    const renderInfo = () => (
        <div className='email-info'>
            <div className='left-items'>
                <span>{displayDate}</span>
                {smsMessage.sent_by_user_name && (
                    <>
                        <span style={{ margin: '0 5px' }}>•</span>
                        <span>{smsMessage.sent_by_user_name}</span>
                    </>
                )}
                {isScheduled && (
                    <>
                        <span style={{ margin: '0 5px' }}>•</span>
                        <span className='sms-cancel-link' onClick={handleCancel}>
                            {t('timeline.sms.cancel_label')}
                        </span>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <div className='timeline-item email sms'>
            {placement === 'page' && (
                <>
                    <div className='marker-container'>
                        <div className='big-marker sms'>
                            <FontAwesomeIcon icon={faMobile as IconProp} className='note-icon fa-fw' />
                        </div>
                    </div>
                    <div className='email-content'>
                        <div className='email-header'>
                            {renderTitle()}
                            {renderInfo()}
                        </div>
                    </div>
                </>
            )}
            {placement === 'summary' && (
                <div className='email-content'>
                    <div className='marker-container'>
                        <div className='medium-marker sms'>
                            <FontAwesomeIcon icon={faMobile as IconProp} className='note-icon fa-fw' />
                        </div>
                    </div>
                    <div>
                        {renderTitle()}
                        {renderInfo()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimelineSms;
